/**
 * Chatbot Floating Widget — Spec Bölüm 6.5 + S4.5
 *
 * Sağ alt köşeye yapışık, manager dashboard'da görünür.
 * - Açıldığında geçmişi gösterir (sessionId localStorage'ta tutulur)
 * - Mesaj gönderir, AI yanıtı + suggested actions (book_calendly, vb.)
 * - Türkçe placeholder, ENTER tuşu submit
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { trackEngagement } from '../lib/engagement.js';

const SESSION_KEY = 'swskills:chat-session';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt?: string;
}

interface SuggestedAction {
  type: 'book_calendly' | 'view_recommendation' | 'view_candidate';
  label: string;
  payload?: Record<string, unknown>;
}

interface ChatResponse {
  sessionId: string;
  message: { id: string; content: string; createdAt: string };
  suggestedActions?: SuggestedAction[];
  inferredIntent?: string;
  engagementScore: number;
  costUsd: number;
}

export function ChatWidget(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>(
    [],
  );
  const listRef = useRef<HTMLDivElement>(null);

  // Session'ı localStorage'tan yükle (yeni sekmede de devam etsin)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (saved) setSessionId(saved);
    } catch {
      // ignore
    }
  }, []);

  // Açıldığında geçmişi yükle
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<{ messages: ChatMessage[] }>(
          `/api/manager/chat/${sessionId}`,
        );
        if (cancelled) return;
        setMessages(result.messages);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'NOT_FOUND') {
          // Eski sessionId artık geçersiz — temizle
          localStorage.removeItem(SESSION_KEY);
          setSessionId(null);
          setMessages([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId]);

  // Otomatik scroll
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  async function send(): Promise<void> {
    if (!input.trim() || sending) return;
    const userText = input.trim();
    setInput('');
    setError(null);
    setSending(true);

    const userMsg: ChatMessage = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: userText,
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const result = await api.post<ChatResponse>('/api/manager/chat', {
        sessionId: sessionId ?? undefined,
        message: userText,
      });
      if (!sessionId) {
        setSessionId(result.sessionId);
        try {
          localStorage.setItem(SESSION_KEY, result.sessionId);
        } catch {
          // ignore quota
        }
      }
      setMessages((prev) => [
        ...prev,
        {
          id: result.message.id,
          role: 'assistant',
          content: result.message.content,
          createdAt: result.message.createdAt,
        },
      ]);
      setSuggestedActions(result.suggestedActions ?? []);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Yanıt alınamadı');
    } finally {
      setSending(false);
    }
  }

  function handleActionClick(action: SuggestedAction): void {
    if (action.type === 'book_calendly') {
      const url = (action.payload?.['url'] as string) ?? 'https://calendly.com/bmuworks';
      trackEngagement('calendly_clicked', { from: 'chat_suggestion' });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setSuggestedActions([]);
  }

  function startNewSession(): void {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    setSessionId(null);
    setMessages([]);
    setSuggestedActions([]);
    setError(null);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          trackEngagement('chatbot_engaged');
        }}
        className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-blue-900 text-2xl text-white shadow-lg hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label="Yardım sohbeti"
      >
        💬
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-30 flex h-[32rem] w-[22rem] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-blue-900 px-4 py-3 text-white">
        <div>
          <p className="text-sm font-semibold">BMU Asistan</p>
          <p className="text-xs text-blue-100">
            Sınav, rapor ve eğitim konularında yardımcı oluyorum
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={startNewSession}
            className="rounded p-1 text-xs hover:bg-blue-800"
            title="Yeni sohbet"
          >
            ↻
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-lg hover:bg-blue-800"
            aria-label="Kapat"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-3 text-sm"
      >
        {messages.length === 0 && (
          <div className="rounded-lg bg-white px-3 py-2 text-slate-700 shadow-sm">
            <p>Merhaba! Size nasıl yardımcı olabilirim?</p>
            <p className="mt-2 text-xs text-slate-500">
              Örnek: "Ekibimin en zayıf olduğu konu neydi?", "Sektör
              ortalaması nedir?", "Eğitim önerisi alabilir miyim?"
            </p>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${
              m.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 ${
                m.role === 'user'
                  ? 'bg-blue-900 text-white'
                  : 'bg-white text-slate-700 shadow-sm'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-white px-3 py-2 text-sm text-slate-500 shadow-sm">
              <span className="inline-flex gap-1">
                <span className="animate-bounce">•</span>
                <span className="animate-bounce delay-100">•</span>
                <span className="animate-bounce delay-200">•</span>
              </span>
            </div>
          </div>
        )}
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </p>
        )}
      </div>

      {/* Suggested actions */}
      {suggestedActions.length > 0 && (
        <div className="border-t border-slate-200 bg-white px-4 py-2">
          <p className="mb-1 text-xs text-slate-500">Öneri:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedActions.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleActionClick(a)}
                className="rounded-full border border-blue-900 px-3 py-1 text-xs font-medium text-blue-900 hover:bg-blue-50"
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-slate-200 bg-white px-3 py-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
          className="flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Mesajınızı yazın…"
            rows={2}
            maxLength={2000}
            className="flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="h-9 rounded-lg bg-blue-900 px-3 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Gönder
          </button>
        </form>
      </div>
    </div>
  );
}
